import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PlansService } from '../../../core/services/plans.service';
import { AuthService } from '../../../core/services/auth.service';
import { WaitlistService } from '../../../core/services/waitlist.service';
import { YouTubePlayerComponent } from '../../../core/shared/components/youtube-player/youtube-player.component';
import { GenericInformation } from '../../../core/models/generic-information.interface';
import { environment } from '../../../../environments/environment';
import Swal from 'sweetalert2';

type PlanPeriod = 'anual' | 'trimestral';

// Interfaz extendida para los planes con detalles
interface PlanData extends GenericInformation {
  details?: string[];
  onDemand?: string[];
  highlight?: string;
}

interface PlanFeature {
  description: string;
  freemium: 'check' | 'cross';
  professional: 'check' | 'cross';
  executive: 'check' | 'cross';
  isOnDemand?: boolean;
  tooltip?: string;
}

@Component({
  selector: 'app-plans-view',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule, YouTubePlayerComponent],
  templateUrl: './plans-view.component.html',
  styleUrls: ['./plans-view.component.scss']
})
export class PlansViewComponent implements OnInit {
  subscriptionTypes: PlanData[] = [];
  loading = false;
  error: string | null = null;
  
  // Planes individuales
  freemiumPlan: PlanData | undefined;
  professionalPlan: PlanData | undefined;
  executivePlan: PlanData | undefined;
  
  // Periodo seleccionado
  selectedPeriod: PlanPeriod = 'anual';
  
  // Descuento anual
  annualDiscount = 15;
  
  // Lista de características (se generará dinámicamente)
  featuresList: PlanFeature[] = [];
  
  // Precios de planes según periodo
  planPrices: {[key: string]: {[key: string]: number}} = {};

  // ===== PROPIEDADES DEL MODAL DE LISTA DE ESPERA =====
  
  // Modal de lista de espera
  showWaitlistModal: boolean = false;
  isSubmittingWaitlist: boolean = false;
  waitlistForm = {
    nombre: '',
    apellido: '',
    telefono: '',
    correoElectronico: '',
    provincia: '',
    distrito: ''
  };

  // Provincias y distritos para el formulario de lista de espera
  provincias = [
    'Lima',
    'Arequipa',
    'Cusco',
    'La Libertad',
    'Piura',
    'Lambayeque',
    'Junín',
    'Cajamarca',
    'Puno',
    'Ica',
    'Ancash',
    'Tacna',
    'San Martín',
    'Loreto',
    'Ucayali',
    'Huánuco',
    'Ayacucho',
    'Moquegua',
    'Pasco',
    'Tumbes',
    'Madre de Dios',
    'Huancavelica',
    'Apurímac',
    'Amazonas',
    'Callao'
  ];

  distritos: { [key: string]: string[] } = {
    'Lima': [
      'Lima', 'Ate', 'Barranco', 'Breña', 'Carabayllo', 'Chaclacayo', 'Chorrillos', 'Cieneguilla',
      'Comas', 'El Agustino', 'Independencia', 'Jesús María', 'La Molina', 'La Victoria', 'Lince',
      'Los Olivos', 'Lurigancho', 'Lurín', 'Magdalena del Mar', 'Miraflores', 'Pachacámac',
      'Pucusana', 'Pueblo Libre', 'Puente Piedra', 'Punta Hermosa', 'Punta Negra', 'Rímac',
      'San Bartolo', 'San Borja', 'San Isidro', 'San Juan de Lurigancho', 'San Juan de Miraflores',
      'San Luis', 'San Martín de Porres', 'San Miguel', 'Santa Anita', 'Santa María del Mar',
      'Santa Rosa', 'Santiago de Surco', 'Surquillo', 'Villa El Salvador', 'Villa María del Triunfo'
    ],
    'Arequipa': [
      'Arequipa', 'Alto Selva Alegre', 'Cayma', 'Cerro Colorado', 'Characato', 'Chiguata',
      'Jacobo Hunter', 'La Joya', 'Mariano Melgar', 'Miraflores', 'Mollebaya', 'Paucarpata',
      'Pocsi', 'Polobaya', 'Quequeña', 'Sabandía', 'Sachaca', 'San Juan de Siguas',
      'San Juan de Tarucani', 'Santa Isabel de Siguas', 'Santa Rita de Siguas', 'Socabaya',
      'Tiabaya', 'Uchumayo', 'Vitor', 'Yanahuara', 'Yarabamba', 'Yura'
    ],
    'Cusco': [
      'Cusco', 'Ccorca', 'Poroy', 'San Jerónimo', 'San Sebastian', 'Santiago', 'Wanchaq'
    ]
  };

  constructor(
    private plansService: PlansService,
    private router: Router,
    private authService: AuthService,
    private waitlistService: WaitlistService
  ) {}

  ngOnInit(): void {
    this.loadSubscriptionTypes();
  }

  loadSubscriptionTypes(): void {
    this.loading = true;
    this.error = null;
    
    this.plansService.getSubscriptionTypes().subscribe({
      next: (response: any) => {
        // Comprobar si la respuesta tiene la estructura esperada
        if (response && response.data && Array.isArray(response.data)) {
          this.subscriptionTypes = response.data as PlanData[];
        } else if (response && Array.isArray(response)) {
          this.subscriptionTypes = response as PlanData[];
        } else if (response && response.statusCode === 200 && response.data) {
          this.subscriptionTypes = response.data as PlanData[];
        } else {
          console.error('Formato de respuesta no reconocido', response);
          this.error = 'Formato de datos no reconocido. Por favor, intente de nuevo más tarde.';
          this.loading = false;
          return;
        }
        
        this.initializePlans();
        this.initializePlanPrices();
        this.generateFeaturesList();
        this.loading = false;
      },
      error: (err) => {
        console.error('Error al cargar los tipos de suscripción', err);
        this.error = 'No se pudieron cargar los programas. Por favor, intente de nuevo más tarde.';
        this.loading = false;
      }
    });
  }
  
  initializePlans(): void {
    // Asignar los planes a sus respectivas variables
    this.freemiumPlan = this.findPlanByName('freemium');
    this.professionalPlan = this.findPlanByName('professional');
    this.executivePlan = this.findPlanByName('executive');
  }
  
  findPlanByName(searchTerm: string): PlanData | undefined {
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    return this.subscriptionTypes.find(plan => {
      if (!plan.name) return false;
      const planName = plan.name.toLowerCase();
      
      if (lowerSearchTerm === 'freemium' && (planName.includes('free') || planName.includes('freemium'))) {
        return true;
      } else if (lowerSearchTerm === 'professional' && (planName.includes('pro') || planName.includes('professional'))) {
        return true;
      } else if (lowerSearchTerm === 'executive' && (planName.includes('exe') || planName.includes('executive'))) {
        return true;
      }
      
      return false;
    });
  }
  
  // Genera la lista de características para la comparación
  generateFeaturesList(): void {
    // Conjunto para almacenar todas las características únicas
    const allFeatures = new Set<string>();
    
    // Recopilar todas las características estándar de todos los planes
    this.subscriptionTypes.forEach(plan => {
      if (plan.details) {
        plan.details.forEach(detail => allFeatures.add(detail));
      }
    });
    
    // Recopilar todas las características "a demanda"
    this.subscriptionTypes.forEach(plan => {
      if (plan.onDemand) {
        plan.onDemand.forEach(feature => allFeatures.add(`${feature} (A demanda)`));
      }
    });
    
    // Generar la lista de características para la tabla
    this.featuresList = Array.from(allFeatures).map(feature => {
      const isOnDemand = feature.includes('(A demanda)');
      const cleanFeature = isOnDemand ? feature.replace(' (A demanda)', '') : feature;
      
      return {
        description: feature,
        freemium: this.planHasFeature('freemium', cleanFeature, isOnDemand) ? 'check' : 'cross',
        professional: this.planHasFeature('professional', cleanFeature, isOnDemand) ? 'check' : 'cross',
        executive: this.planHasFeature('executive', cleanFeature, isOnDemand) ? 'check' : 'cross',
        isOnDemand
      };
    });
    
    // Ordenar las características: primero las estándar, luego las "a demanda"
    this.featuresList.sort((a, b) => {
      if (a.isOnDemand && !b.isOnDemand) return 1;
      if (!a.isOnDemand && b.isOnDemand) return -1;
      return 0;
    });
  }
  
  // Verifica si un plan tiene una característica específica
  planHasFeature(planType: string, feature: string, isOnDemand: boolean): boolean {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
      default:
        return false;
    }
    
    if (!plan) return false;
    
    if (isOnDemand) {
      return !!plan.onDemand && plan.onDemand.includes(feature);
    } else {
      return !!plan.details && plan.details.includes(feature);
    }
  }
  
  // Inicializa los precios basados en los datos recibidos
  initializePlanPrices(): void {
    // Freemium es siempre gratuito
    this.planPrices['freemium'] = {
      anual: 0,
      trimestral: 0
    };
    
    // Professional
    if (this.professionalPlan) {
      const monthlyPrice = parseFloat(this.professionalPlan.price || '0');
      this.planPrices['professional'] = {
        trimestral: monthlyPrice * 3,
        anual: monthlyPrice * 12 * (1 - this.annualDiscount / 100)
      };
    }
    
    // Executive
    if (this.executivePlan) {
      const monthlyPrice = parseFloat(this.executivePlan.price || '0');
      this.planPrices['executive'] = {
        trimestral: monthlyPrice * 3,
        anual: monthlyPrice * 12 * (1 - this.annualDiscount / 100)
      };
    }
  }
  
  // Cambia entre periodo anual y trimestral
  togglePeriod(period: PlanPeriod): void {
    this.selectedPeriod = period;
  }
  
  // Obtiene el precio total según el plan y periodo seleccionado
  getTotalPrice(planType: string): number {
    if (!this.planPrices[planType] || !this.planPrices[planType][this.selectedPeriod]) {
      return 0;
    }
    
    return Math.round(this.planPrices[planType][this.selectedPeriod]);
  }
  
  // Métodos para obtener las características de un plan
  getPlanDetails(planType: string): string[] {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    return plan?.details || [];
  }
  
  getPlanOnDemandFeatures(planType: string): string[] {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    return plan?.onDemand || [];
  }
  
  // Verifica si un plan tiene badge destacado
  hasPlanHighlight(planType: string): boolean {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    return !!plan?.highlight;
  }
  
  // Obtiene el texto del badge destacado
  getPlanHighlightText(planType: string): string {
    let plan: PlanData | undefined;
    
    switch (planType) {
      case 'freemium':
        plan = this.freemiumPlan;
        break;
      case 'professional':
        plan = this.professionalPlan;
        break;
      case 'executive':
        plan = this.executivePlan;
        break;
    }
    
    return plan?.highlight || '';
  }
  
  // ===== MÉTODOS DEL MODAL DE LISTA DE ESPERA =====
  
  // Abrir el modal de lista de espera
  openWaitlistModal(): void {
    this.showWaitlistModal = true;
    this.resetWaitlistForm();
    
    // Pre-llenar con datos del usuario si está autenticado
    const user = this.authService.getAuthUser();
    if (user) {
      this.waitlistForm.nombre = user.name || '';
      this.waitlistForm.apellido = user.lastName || '';
      this.waitlistForm.correoElectronico = user.email || '';
      // El teléfono se debe llenar manualmente
    }
  }

  // Cerrar el modal de lista de espera
  closeWaitlistModal(): void {
    this.showWaitlistModal = false;
    this.resetWaitlistForm();
  }

  // Resetear el formulario de lista de espera
  resetWaitlistForm(): void {
    this.waitlistForm = {
      nombre: '',
      apellido: '',
      telefono: '',
      correoElectronico: '',
      provincia: '',
      distrito: ''
    };
    this.isSubmittingWaitlist = false;
  }

  // Obtener distritos por provincia
  getDistritosByProvincia(): string[] {
    if (!this.waitlistForm.provincia) {
      return [];
    }
    return this.distritos[this.waitlistForm.provincia] || [];
  }

  // Manejar cambio de provincia
  onProvinciaChange(): void {
    this.waitlistForm.distrito = ''; // Resetear distrito cuando cambia la provincia
  }

  // Validar formulario de lista de espera
  isWaitlistFormValid(): boolean {
    return !!(
      this.waitlistForm.nombre &&
      this.waitlistForm.apellido &&
      this.waitlistForm.telefono &&
      this.waitlistForm.correoElectronico &&
      this.waitlistForm.provincia &&
      this.waitlistForm.distrito
    );
  }

  // Función auxiliar para mostrar alertas
  private alert(title: string, text: string, icon: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#e41e3f'
    });
  }

  // Enviar formulario de lista de espera
  submitWaitlistForm(): void {
    if (!this.isWaitlistFormValid()) {
      this.alert('Formulario incompleto', 'Por favor completa todos los campos requeridos.', 'warning');
      return;
    }

    this.isSubmittingWaitlist = true;

    const waitlistData = {
      ...this.waitlistForm,
      createdAt: new Date().toISOString()
    };
    
    this.waitlistService.registerToWaitlist(waitlistData).subscribe({
      next: (response: any) => {
        this.isSubmittingWaitlist = false;
        this.alert(
          '¡Registro exitoso!',
          'Te has registrado exitosamente en nuestra lista de espera. Te contactaremos pronto.',
          'success'
        );
        this.closeWaitlistModal();
      },
      error: (error: any) => {
        this.isSubmittingWaitlist = false;
        console.error('Error al registrar en lista de espera:', error);
        this.alert(
          'Error en el registro',
          'Hubo un problema al registrarte en la lista de espera. Por favor intenta nuevamente.',
          'error'
        );
      }
    });
  }

  // Obtener URL del video profesional
  getProfessionalVideoUrl(): string {
    return environment.urlVideoProfessional || '';
  }

  // Extraer ID del video de YouTube
  getProfessionalVideoId(): string | null {
    const url = this.getProfessionalVideoUrl();
    if (!url) return null;

    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  }

  // Obtener parámetros del video
  getVideoParams(): any {
    return {
      autoplay: 0,
      controls: 1,
      rel: 0,
      modestbranding: 1
    };
  }

  // Manejar la suscripción a un plan
  subscribeToPlan(planType: string): void {
    // Para el plan freemium no hay pago
    if (planType === 'freemium') {
      // Implementar lógica para plan gratuito (redirigir al dashboard)
      this.router.navigate(['/dashboard']);
      return;
    }

    // Para el plan professional, abrir el modal de lista de espera
    if (planType === 'professional') {
      this.openWaitlistModal();
      return;
    }

    // Para el plan executive, redirigir a WhatsApp
    if (planType === 'executive') {
      this.redirectToWhatsApp();
      return;
    }

    const user = this.authService.getAuthUser();
    if (!user || !user.id) {
      console.error('Usuario no autenticado. No se puede proceder al pago.');
      // Opcional: redirigir a login
      // this.router.navigate(['/login']);
      return;
    }
    
    // Verificar que el plan exista
    if (!this.planPrices[planType] || !this.planPrices[planType][this.selectedPeriod]) {
      console.error(`Plan "${planType}" no encontrado o no disponible para ${this.selectedPeriod}`);
      return;
    }
    
    // Obtener precio y nombre del plan
    const planPrice = this.getTotalPrice(planType);
    let planName = planType.charAt(0).toUpperCase() + planType.slice(1);
    
    // Usar el nombre del plan desde la API si está disponible
    if (planType === 'professional' && this.professionalPlan) {
      planName = this.professionalPlan.name;
    } else if (planType === 'executive' && this.executivePlan) {
      planName = this.executivePlan.name;
    }
    
    // Redirigir a la página de pago (dentro del dashboard)
    this.router.navigate(['/dashboard/payment'], {
      queryParams: {
        userId: user.id,
        planId: planType,
        planName: planName,
        amount: planPrice,
        currency: 'PEN',
        frequency: this.selectedPeriod
      }
    });
  }

  // Redirigir a WhatsApp para el plan executive
  redirectToWhatsApp(): void {
    // Número de WhatsApp (formato internacional sin +)
    const whatsappNumber = '51987654321'; // Reemplazar con el número real
    
    // Mensaje predefinido para el plan executive
    const message = encodeURIComponent(
      'Hola! Me interesa el plan Executive de Phoenix. Me gustaría agendar una entrevista para conocer más detalles sobre el programa.'
    );
    
    // URL de WhatsApp Web/App
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;
    
    // Abrir WhatsApp en una nueva ventana
    window.open(whatsappUrl, '_blank');
  }
}